import React from 'react';
import { X } from 'lucide-react';
import { VerdictPill } from './VerdictPill';
import { EffortTagPill } from './EffortTagPill';
import type { ExplorerRowBrief } from './ExplorerRow';

export interface ExplorerCompareTableProps {
  briefs: ExplorerRowBrief[];
  onFocus: (id: string) => void;
  onRemove: (id: string) => void;
  portfolioIds: string[];
}

export const ExplorerCompareTable: React.FC<ExplorerCompareTableProps> = ({
  briefs,
  onFocus,
  onRemove,
  portfolioIds,
}) => {
  if (briefs.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: `140px repeat(${briefs.length}, minmax(0, 1fr))` }}>
        <div className="px-4 py-3 bg-[#F5F7FA] border-b border-[#e5e7eb] text-[10px] font-semibold text-[#717182] uppercase">Metric</div>
        {briefs.map((b) => (
          <div key={b.id} className="px-4 py-3 bg-[#F5F7FA] border-b border-l border-[#e5e7eb] flex items-start justify-between gap-2">
            <button type="button" onClick={() => onFocus(b.id)} className="text-left">
              <p className="text-xs font-bold text-[#18365E] leading-snug">{b.title}</p>
              <p className="text-[10px] text-[#717182] mt-0.5">#{b.rank}</p>
            </button>
            <button type="button" onClick={() => onRemove(b.id)} aria-label={`Remove ${b.title}`} className="text-[#717182] hover:text-[#18365E]">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        <div className="px-4 py-2.5 text-[11px] text-[#717182] border-b border-[#e5e7eb]">Executive verdict</div>
        {briefs.map((b) => (
          <div key={`${b.id}-v`} className="px-4 py-2.5 border-b border-l border-[#e5e7eb]"><VerdictPill verdict={b.verdict} /></div>
        ))}

        <div className="px-4 py-2.5 text-[11px] text-[#717182] border-b border-[#e5e7eb]">Est. value</div>
        {briefs.map((b) => (
          <div key={`${b.id}-r`} className="px-4 py-2.5 border-b border-l border-[#e5e7eb] text-sm font-bold text-[#18365E]">{b.roiShort}/yr</div>
        ))}

        <div className="px-4 py-2.5 text-[11px] text-[#717182] border-b border-[#e5e7eb]">Time to value</div>
        {briefs.map((b) => (
          <div key={`${b.id}-t`} className="px-4 py-2.5 border-b border-l border-[#e5e7eb] text-xs text-[#18365E]">{b.ttv}</div>
        ))}

        <div className="px-4 py-2.5 text-[11px] text-[#717182] border-b border-[#e5e7eb]">Effort</div>
        {briefs.map((b) => (
          <div key={`${b.id}-e`} className="px-4 py-2.5 border-b border-l border-[#e5e7eb]"><EffortTagPill tag={b.effortTag} /></div>
        ))}

        <div className="px-4 py-3 text-[11px] text-[#717182]">Action</div>
        {briefs.map((b) => (
          <div key={`${b.id}-a`} className="px-4 py-3 border-l border-[#e5e7eb]">
            <button
              type="button"
              onClick={() => onFocus(b.id)}
              className="text-xs font-semibold text-[#18365E] hover:text-[#FF6E4C]"
            >
              {portfolioIds.includes(b.id) ? 'Open brief · in portfolio' : 'Open brief'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExplorerCompareTable;
